import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Paper, 
  Badge,
  Tooltip,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

const InputSection = ({ 
  onSendMessage, 
  onVoiceInput, 
  onToggleHints,
  showHints,
  isProcessing,
  language,
  inputText,
  setInputText
}) => {
  const theme = useTheme();
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const inputRef = useRef(null);
  
  // Sync with parent component's inputText
  useEffect(() => {
    if (inputText !== undefined) {
      setMessage(inputText);
    }
  }, [inputText]);
  
  // Update parent component with local changes
  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    setMessage(newValue);
    if (setInputText) {
      setInputText(newValue);
    }
  };
  
  // Handle send message
  const handleSendMessage = () => {
    if (message.trim() && !isProcessing) {
      onSendMessage(message);
      setMessage('');
      if (setInputText) {
        setInputText('');
      }
    }
  };
  
  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Toggle voice recording
  const toggleVoiceInput = () => {
    if (isRecording) {
      setIsRecording(false);
      if (recognition) {
        recognition.stop();
        setRecognition(null);
      }
    } else {
      setIsRecording(true);
      const rec = onVoiceInput((transcript) => {
        setMessage(transcript);
        if (setInputText) {
          setInputText(transcript);
        }
      });
      
      if (rec) {
        setRecognition(rec);
      }
    }
  };
  
  // Cleanup recognition on unmount
  useEffect(() => {
    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, [recognition]);
  
  // Get language display name
  const getLanguageDisplay = () => {
    // Map of language codes to display names
    const languageNames = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese',
      'ru': 'Russian',
      'pt': 'Portuguese',
      'ar': 'Arabic',
      'hi': 'Hindi'
    };
    
    return languageNames[language] || language;
  };
  
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        borderRadius: '0 0 16px 16px',
        bgcolor: theme.palette.background.paper
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Removed suggestions button per requirement #3 */}
        
        {/* Text input */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={`Type your message in ${getLanguageDisplay()}...`}
          value={message}
          onChange={handleMessageChange}
          onKeyPress={handleKeyPress}
          disabled={isProcessing || isRecording}
          size="small"
          inputRef={inputRef}
          multiline
          maxRows={4}
          InputProps={{
            sx: { 
              borderRadius: 3,
              backgroundColor: theme.palette.background.default,
              '&.Mui-focused': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
              }
            }
          }}
        />
        
        {/* Voice input button */}
        <Tooltip title={isRecording ? "Stop recording" : "Voice input"}>
          <IconButton 
            color={isRecording ? "error" : "default"} 
            onClick={toggleVoiceInput}
            disabled={isProcessing}
            sx={{
              bgcolor: isRecording ? 'error.light' : 'transparent',
              '&:hover': {
                bgcolor: isRecording ? 'error.light' : 'action.hover',
              }
            }}
          >
            <Badge
              variant="dot"
              color={isRecording ? "error" : "success"}
              invisible={!isRecording}
            >
              {isRecording ? <StopIcon /> : <MicIcon />}
            </Badge>
          </IconButton>
        </Tooltip>
        
        {/* Send button */}
        <Tooltip title="Send message">
          <span> {/* Wrapper to handle disabled state */}
            <IconButton 
              color="primary" 
              onClick={handleSendMessage}
              disabled={!message.trim() || isRecording || isProcessing}
              sx={{
                bgcolor: message.trim() && !isRecording && !isProcessing ? 'primary.main' : 'action.disabledBackground',
                color: message.trim() && !isRecording && !isProcessing ? 'primary.contrastText' : 'text.disabled',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default InputSection;